import { Domain, HOSTNAMES_BY_ENV } from "./constants";
import { getHostnameDomain, getRegionDomain } from "./getDomain";

const isMissingKey = (value?: string) => {
  return !value || value === "undefined";
};

async function fetchGeopsPublicKey() {
  if (import.meta.env.MODE === "test") {
    return undefined;
  }
  try {
    const response = await fetch("https://developer.geops.io/publickey");
    if (!response.ok) {
      return undefined;
    }
    const { key } = await response.json();
    return key;
  } catch {
    return undefined;
  }
}

const {
  VITE_AUTH_URL,
  VITE_DOMAIN,
  VITE_ENV,
  VITE_GEOAPIFY_KEY,
  VITE_GEOPS_API_URL,
  VITE_GRAPHHOPPER_KEY,
  VITE_ROUTING_KEY,
  VITE_SSO_URL,
  VITE_STOP_FINDER_KEY,
  VITE_VECTOR_TILES_KEY,
  VITE_VECTOR_TILES_URL,
} = import.meta.env;

export default async function getEnvUrlsAndKeys(
  env = VITE_ENV || "prod",
): Promise<{
  apiUrl: string;
  authUrl: string;
  bussesStyleUrl: string;
  domain: Domain;
  feedbackUrl: string;
  geoapifyKey: string;
  geopsApiUrl: string;
  graphhopperKey: string;
  iconsUrl: string;
  imagesUrl: string;
  kmlUrl: string;
  layoutsUrl: string;
  linkShortenerUrl: string;
  mapsetEditorUrl: string;
  mapsetUrl: string;
  meUrl: string;
  oldKmlUrl: string;
  pictosPrefix: string;
  pictosUrl: string;
  routingKey: string;
  routingUrl: string;
  ssoConfigsUrl: string;
  ssoOpenIdUrl: string;
  ssoUrl: string;
  stopFinderKey: string;
  stopFinderUrl: string;
  thumbnailsUrl: string;
  tilesKey: string;
  tilesUrl: string;
  userInfoUrl: string;
}> {
  // const isE2eHost = isE2eHostname(window.location.hostname);
  // const authBaseUrl = VITE_AUTH_URL;
  // const useRelativeLocalAuthUrl =
  //   window.location.hostname === "localhost" &&
  //   /^https?:\/\/localhost(?::\d+)?$/.test(authBaseUrl || "");
  // const resolvedAuthBaseUrl =
  //   isE2eHost || useRelativeLocalAuthUrl ? "" : authBaseUrl;

  const hostNameDomain =
    VITE_DOMAIN || getHostnameDomain(window.location.hostname);
  const mapsetEditorUrl = HOSTNAMES_BY_ENV[`${env}_${hostNameDomain}`];
  const domain = getRegionDomain(mapsetEditorUrl) || Domain.Ch;
  const version = env === "dev" ? "dev" : "v1";
  const devDot = /(dev|stag|test)/i.test(env) ? "dev." : "";
  const devOnlyDot = /dev/i.test(env) ? "dev." : "";

  /* SSO urls */
  const ssoUrl = VITE_SSO_URL || "https://sso.geops.io";
  const ssoConfigsUrl = `${ssoUrl}/api/configs`;
  const ssoOpenIdUrl = `${ssoUrl}/openid`;

  /* Maspet backend urls */
  const backendUrl = VITE_AUTH_URL || window.location.origin;
  // Static images URL must have an absolute urls otherweise the exported KML is broken
  const imagesUrl = `${backendUrl}/static/images/`;
  const thumbnailsUrl = `${backendUrl}/static/thumbnails/`;
  const authUrl = `${backendUrl}/accounts/geops/login`;
  const apiUrl = `${backendUrl}${"/api/v1"}`;
  const userInfoUrl = `${apiUrl}/userinfo`;
  const meUrl = `${apiUrl}/me`;
  const feedbackUrl = `${apiUrl}/feedback-form`;
  const kmlUrl = `${apiUrl}/meta/kml`;
  const layoutsUrl = `${apiUrl}/layouts`;
  const iconsUrl = `${apiUrl}/icons`;

  const imagesUrlForExternalService = `${mapsetEditorUrl}/static/images/`;

  /* geOps APIs urls */
  const geopsApiUrl = VITE_GEOPS_API_URL || "https://api.geops.io";
  const stopFinderUrl = `${geopsApiUrl}/stops/${version}/`;
  const routingUrl = `${geopsApiUrl}/routing/${version}/`;
  const tilesUrl = VITE_VECTOR_TILES_URL || "https://maps.geops.io";
  const bussesStyleUrl = `${tilesUrl}/data/busses.json`;

  /* geOps others services */
  const linkShortenerUrl = `https://${devDot}geops.sh/api/v1/weburls`;
  const mapsetUrl = `https://mapset.${domain}`;
  const oldKmlUrl = "https://maps.trafimage.ch/services/cbs/trafzeich/kml";
  const pictosUrl = `https://icon-generator.${devOnlyDot}geops.io/pictogram`;

  const needsGeopsPublicKey =
    isMissingKey(VITE_VECTOR_TILES_KEY) ||
    isMissingKey(VITE_STOP_FINDER_KEY) ||
    isMissingKey(VITE_ROUTING_KEY);
  const geopsPublicKey = needsGeopsPublicKey
    ? await fetchGeopsPublicKey()
    : undefined;
  const resolvedTilesKey = isMissingKey(VITE_VECTOR_TILES_KEY)
    ? geopsPublicKey
    : VITE_VECTOR_TILES_KEY;
  const resolvedStopFinderKey = isMissingKey(VITE_STOP_FINDER_KEY)
    ? geopsPublicKey
    : VITE_STOP_FINDER_KEY;
  const resolvedRoutingKey = isMissingKey(VITE_ROUTING_KEY)
    ? geopsPublicKey
    : VITE_ROUTING_KEY;

  return {
    apiUrl,
    authUrl,
    bussesStyleUrl,
    domain,
    feedbackUrl,
    geoapifyKey: VITE_GEOAPIFY_KEY || "",
    geopsApiUrl,
    graphhopperKey: VITE_GRAPHHOPPER_KEY || "",
    iconsUrl,
    imagesUrl,
    kmlUrl,
    layoutsUrl,
    linkShortenerUrl,
    mapsetEditorUrl,
    mapsetUrl,
    meUrl,
    oldKmlUrl,
    pictosPrefix: imagesUrlForExternalService,
    pictosUrl,
    routingKey: `${resolvedRoutingKey}`,
    routingUrl,
    ssoConfigsUrl,
    ssoOpenIdUrl,
    ssoUrl,
    stopFinderKey: `${resolvedStopFinderKey}`,
    stopFinderUrl,
    thumbnailsUrl,
    tilesKey: `${resolvedTilesKey}`,
    tilesUrl,
    userInfoUrl,
  };
}
